/**
 * Palpite de tratamento (Dr./Dra.) a partir do prenome — e só isso: um palpite,
 * mostrado ao advogado no wizard para ele confirmar ou trocar em um clique.
 *
 * Por que aqui e não no backend: o núcleo do Secretário NÃO deduz gênero de
 * ninguém. Ele lê `configuracao.advogado_tratamento`, que é um dado escolhido por
 * uma pessoa. A dedução vive nesta borda, onde o resultado é visível e corrigível
 * antes de virar dado — a diferença entre sugerir e decidir por alguém.
 *
 * Sem palpite (`null`) é resposta legítima e frequente: prenome ambíguo em
 * português (Alex, Ariel, Darci) ou desconhecido não vira chute silencioso, vira
 * pergunta de dois botões. Errar o tratamento de quem o cliente vai encontrar
 * pessoalmente é constrangedor; perguntar uma vez, não.
 */

export type Tratamento = 'DR' | 'DRA';

/** Prenomes femininos que NÃO terminam em "a": a terminação sozinha erra neles. */
const FEMININOS = new Set([
  'isabel', 'raquel', 'rachel', 'mabel', 'ester', 'esther', 'nair', 'beatriz', 'ingrid',
  'karen', 'karin', 'miriam', 'mirian', 'doris', 'iris', 'ruth', 'judith', 'edith',
  'elizabeth', 'lilian', 'liliane', 'carmen', 'carmem', 'astrid', 'marimar', 'noemi',
  'noemia', 'jaqueline', 'jacqueline', 'caroline', 'carolyne', 'evelin', 'evelyn',
  'kelly', 'emily', 'nataly', 'nathaly', 'ellen', 'helen', 'heloise', 'eloise',
  'denise', 'simone', 'ivone', 'yvone', 'solange', 'alice', 'clarice', 'berenice',
  'eunice', 'janice', 'joyce', 'gleyce', 'elis', 'ines', 'agnes', 'mercedes',
  'michele', 'michelle', 'gabriele', 'gabrielle', 'daniele', 'danielle', 'cristiane',
  'cristiani', 'luciane', 'adriane', 'rosane', 'josiane', 'viviane', 'juliane',
  'eliane', 'sirlei', 'marlei', 'nadir', 'esmeralda', 'yasmin', 'jasmin', 'sharon',
]);

/** Prenomes masculinos terminados em "a": a terminação sozinha erra neles. */
const MASCULINOS = new Set([
  'luca', 'nicola', 'sacha', 'josua', 'josue', 'noa', 'iva', 'jonha',
  'atila', 'ataliba', 'barnaba', 'juba', 'yuca',
]);

/**
 * Prenomes usados pelos dois gêneros no Brasil. Não há palpite honesto aqui — e é
 * onde uma heurística de terminação erra com mais confiança.
 */
const AMBIGUOS = new Set([
  'alex', 'ariel', 'darci', 'darcy', 'dani', 'eli', 'wesley', 'lesley', 'kim',
  'sasha', 'ale', 'val', 'nil', 'mel', 'sol', 'cris', 'chris', 'jean', 'jaci',
  'jacy', 'iraci', 'iracy', 'juca', 'lu', 'noel', 'rene', 'remi',
  'jordan', 'robin', 'ryan', 'nikita', 'andrea',
]);

/** Terminações que, no português brasileiro, quase sempre indicam masculino. */
const FINAIS_MASCULINOS = ['o', 'os', 'or', 'io', 'son', 'ton', 'nho', 'ico'];

const semAcento = (texto: string) => texto.normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * Palpite para o nome completo, ou `null` quando não dá para afirmar.
 *
 * Olha só o PRIMEIRO nome: é ele que carrega o gênero. Sobrenome não ("Silva"
 * termina em "a" e não diz nada sobre quem o usa).
 */
export function tratamentoProvavel(nomeCompleto: string | null | undefined): Tratamento | null {
  const prenome = semAcento((nomeCompleto ?? '').trim().split(/\s+/)[0] ?? '').toLowerCase();
  if (prenome.length < 2) return null;

  if (AMBIGUOS.has(prenome)) return null;
  if (FEMININOS.has(prenome)) return 'DRA';
  if (MASCULINOS.has(prenome)) return 'DR';

  if (prenome.endsWith('a')) return 'DRA';
  if (FINAIS_MASCULINOS.some((final) => prenome.endsWith(final))) return 'DR';

  // Consoante final (Rafael, Vitor, Marcos) é majoritariamente masculina, mas a
  // exceção feminina é comum demais para chutar fora da lista acima.
  if (/[bcdfgjklmnpqrstvwxz]$/.test(prenome)) return 'DR';

  // Terminações em -e/-i/-u são as mais disputadas (Felipe × Denise, Eloi ×
  // Naomi): sem lista, não há palpite.
  return null;
}

/** "Dra. Marimar Pereira de Araujo" — o nome como o cliente vai lê-lo. */
export function comTratamento(nome: string, tratamento: Tratamento | '' | null | undefined): string {
  if (tratamento === 'DR') return `Dr. ${nome}`;
  if (tratamento === 'DRA') return `Dra. ${nome}`;
  return nome;
}
